/**
 * <s-terms-check> — Required terms checkbox for the cart's checkout form.
 *
 * Required child markup:
 *   input[type="checkbox"][required] — The acceptance checkbox inside the cart form.
 *   [data-terms-error]               — Visible error message, hidden until checkout is blocked.
 *
 * Element dataset:
 *   data-message — Translated validation message applied to the checkbox.
 *
 * The browser's native form validation blocks the checkout submit while the box is
 * unticked; this element replaces the native bubble with the visible error message.
 */
class STermsCheck extends HTMLElement {
  #input;
  #error;
  #ac;

  connectedCallback() {
    this.#input = this.querySelector('input[type="checkbox"]');
    this.#error = this.querySelector('[data-terms-error]');
    if (!this.#input) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#sync();
    this.#input.addEventListener(
      'invalid',
      (event) => {
        event.preventDefault();
        this.#showError(true);
        this.#input.focus();
      },
      { signal },
    );
    this.#input.addEventListener(
      'change',
      () => {
        this.#sync();
        if (this.#input.checked) this.#showError(false);
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    this.#input.setCustomValidity(this.#input.checked ? '' : this.dataset.message || '');
  }

  #showError(visible) {
    if (!this.#error) return;
    this.#error.hidden = !visible;
    this.#input.setAttribute('aria-invalid', visible ? 'true' : 'false');
  }
}

if (!customElements.get('s-terms-check')) {
  customElements.define('s-terms-check', STermsCheck);
}
